import {throwError as observableThrowError, Observable, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {CookieService} from 'web-common/src/app/cookie.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {AdminCreationDatamodel} from '../datamodel/admin-creation.datamodel';

@Injectable()
export class AccountCreationService {
  public availableRoles = ['Adjunct/Affiliate Faculty', 'Administrator', 'Allied Health Worker', 'Alumni', 'Author',
    'Clinical', 'Consultant', 'Development Personnel', 'Editor', 'Executive', 'Faculty', 'Fellow', 'Grant Writer', 'Instructor',
    'Lecturer', 'Librarian', 'Manager', 'Marketing Personnel', 'Medical Doctor', 'Nurse', 'Peer Reviewer',
    'Physician', 'Postdoctoral', 'Professor', 'Professor Emeritus', 'Professor, Assistant', 'Professor, Associate', 'Publisher',
    'Reader', 'Researcher', 'Scientist', 'Senior Lecturer', 'Staff', 'Student, Doctoral Candidate', 'Student, Graduate',
    'Student, Postgraduate', 'Student, Undergraduate', 'Systematic Reviewer', 'Other'];

  public availableAreaOfFocus = ['Accounting', 'Acupuncture', 'Administration', 'Adult Learning', 'Advertising',
    'African American Studies', 'Agriculture', 'Allied Health', 'Anatomy', 'Anesthesiology', 'Anthropology',
    'Applied Mathematics', 'Applied Physics', 'Applied Sciences', 'Architecture', 'Art', 'Audiology', 'Aviation',
    'Behavioral Science', 'Biblical Studies', 'Bioengineering', 'Biology', 'Business', 'Business Administration', 'Business Intelligence',
    'Chinese Medicine', 'Christian Ministry', 'Climate Sciences', 'Communication', 'Community Planning', 'Computer Science',
    'Conflict Resolution', 'Consumer Sciences', 'Criminal Justice', 'Criminology', 'Dance', 'Dentistry', 'Design', 'Development',
    'Diplomacy', 'Drama', 'Economics', 'Education', 'Engineering', 'English', 'Entomology', 'Environmental Sciences',
    'Family Science', 'Fashion', 'Film', 'Finance', 'Fine Arts', 'Food Sciences', 'Forensic Sciences',
    'Gender Studies', 'Geography', 'Geology', 'Government', 'Health Sciences', 'History', 'Hospital Administration',
    'Hospitality', 'Human Resources', 'Human Sciences', 'Information Technology', 'Innovation', 'International Relations',
    'International Studies', 'Journalism', 'Languages', 'Law', 'Leadership', 'Legal Studies', 'Librarianship', 'Life Sciences',
    'Linguistics', 'Literature', 'Management', 'Marketing', 'Marketing Intelligence', 'Mediation', 'Music',
    'Neuroscience', 'Nursing', 'Nutrition', 'Occupational Therapy', 'Operations', 'Orthopedics', 'Patent',
    'Pharmacology', 'Philosophy', 'Political Science', 'Procurement', 'Psychology', 'Public Affairs', 'Public Health',
    'Radiology', 'Regulatory', 'Religion', 'Research', 'Rocket Science', 'Science', 'Social Sciences', 'Sociology',
    'Theater', 'Theatre Arts', 'Urban Planning', 'Veterinary Medicine', 'Visual Arts', 'Women & Gender Studies',
    'Zoology', 'Other'];

  public chosenRoles = [];
  public chosenAreaOfFocus = [];
  public updatedRoles = new Subject();
  public updatedAreas = new Subject();

  constructor(
    private http: HttpClient,
    private cookieService: CookieService) {
  }

  public getChosenRoles() {
    return this.chosenRoles;
  }

  public getChosenAreaOfFocus() {
    return this.chosenAreaOfFocus;
  }

  public updateChosenRoles(newRoles) {
    this.chosenRoles = this.removeDuplicate(this.cleanEmptySpaces(newRoles));
  }

  public updateChosenAreaOfFocus(newAof) {
    this.chosenAreaOfFocus = this.removeDuplicate(this.cleanEmptySpaces(newAof));
  }

  public getAvailableRoles() {
    return this.availableRoles;
  }

  public getAvailableAof() {
    return this.availableAreaOfFocus;
  }

  public getCombinedRoles(): string[] {
    const aux = this.getAvailableRoles();
    let lowerCaseAvailableRoles =  aux.map(item => item.toLowerCase());

    let roleList = this.getChosenRoles().filter(item => {
    return !lowerCaseAvailableRoles.includes(item.toLowerCase());
  });
  return aux.concat(roleList);

  }

  public setChosenRolesFromModal(selectedRoles: any[]) {
    this.chosenRoles = selectedRoles;
    this.updatedRoles.next(null);
  }

  public setChosenAreasFromModal(selectedAreaOfFocus: any[]) {
    this.chosenAreaOfFocus = selectedAreaOfFocus;
    this.updatedAreas.next(null);
  }

  private getHeaders(): HttpHeaders {
    let cookieString = 'id=' + this.cookieService.get('id') + '; user_token=' + this.cookieService.get('user_token') + ';';
    let headers = new HttpHeaders()
      .append('cookiestring', cookieString)
      .append('Content-Type', 'application/json')
      .append('apiVersion', '1')
      .append('appid', environment.appId);
    return headers;
  }

  private decodeEntities(arr) {
    for (let i = 0; i < arr.length; i++) {
      let str = arr[i];
      const element = document.createElement('div');
      if (str && typeof str === 'string') {
        str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
        str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
        element.innerHTML = str;
        str = element.textContent;
        element.textContent = '';
      }
      arr[i] = str;
    }
    return arr;
  }

  private removeDuplicate(arr) {
    let mapping = arr.reduce((obj, str) =>
      ({...obj, [str.toLowerCase()]: str}), {});
    return Object.keys(mapping).map(k => mapping[k]);
  }

  public cleanEmptySpaces(str) {
    return str.map(item => {
      return item.trim();
    }).filter(function (el) {
      return el !== '';
    });
  }

  public initializeRolesAndAreasOfFocus(): Observable<any> {
    const that = this;
    return this.http.get<{data: AdminCreationDatamodel}>(environment.rw3apiURL + 'institution/rolesAndAreaOfFocus',
      {headers: this.getHeaders()})
      .pipe(map(function (res) {
        const roles = that.decodeEntities(that.removeDuplicate(res.data.roles));
        that.chosenRoles = roles;
        that.updatedRoles.next(null);
        const areaOfFocus = that.decodeEntities(that.removeDuplicate(res.data.areaOfFocus));
        that.chosenAreaOfFocus = areaOfFocus;
        that.updatedAreas.next(null);
      }), catchError(this.handleError));
  }


  public saveRolesAndAreasOfFocus(newRoles: String [], newAreas: String []): Observable<any> {
    this.updateChosenRoles(newRoles);
    this.updateChosenAreaOfFocus(newAreas);
    const data = new AdminCreationDatamodel(this.chosenRoles, this.chosenAreaOfFocus);
    return this.http.post(environment.rw3apiURL + 'institution/rolesAndAreaOfFocus', data, {headers: this.getHeaders()})
    .pipe(map(function () {return}), catchError(this.handleError));
  }

  public handleError (error: any) {
    // TODO: Use a remote logging infrastructure
    let errMsg: string;
    if (error.status) {
      const body = error || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return observableThrowError(errMsg);
  }

  public getCombinedAof() {
    const aux = this.getAvailableAof();
    let lowerCaseAvailableRoles =  aux.map(item => item.toLowerCase());

    let aofList = this.getChosenAreaOfFocus().filter(item => {
      return !lowerCaseAvailableRoles.includes(item.toLowerCase());
    });
    return aux.concat(aofList);
  }
}
